import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/auth.store';
import { toast } from 'react-hot-toast';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';
import { decodeCrmOAuthState } from '@/features/auth/utils/crm-oauth-state.util';

const HANDLED_CRM_OAUTH_CODES_STORAGE_KEY = 'handled-crm-oauth-codes';

const getHandledCrmOauthCodes = (): Set<string> => {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const storedValue = window.sessionStorage.getItem(HANDLED_CRM_OAUTH_CODES_STORAGE_KEY);
    if (!storedValue) {
      return new Set();
    }

    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? new Set(parsedValue.filter(Boolean)) : new Set();
  } catch (error) {
    logger.warn('CrmCallback', 'Failed to read handled CRM OAuth codes from session storage', {
      error,
    });
    return new Set();
  }
};

const markCrmOauthCodeHandled = (code: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  const handledCodes = getHandledCrmOauthCodes();
  handledCodes.add(code);

  try {
    window.sessionStorage.setItem(
      HANDLED_CRM_OAUTH_CODES_STORAGE_KEY,
      JSON.stringify(Array.from(handledCodes))
    );
  } catch (error) {
    logger.warn('CrmCallback', 'Failed to persist handled CRM OAuth code', {
      error,
      codePreview: `${code.slice(0, 6)}...`,
    });
  }
};

export const CrmCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasHandledCallback = useRef(false);
  const {
    handleCrmCallback,
    isLoading: isAuthLoading,
    isAuthenticated,
    selectedProvider,
    setSelectedProvider,
  } = useAuthStore();

  useEffect(() => {
    const handleCallback = async () => {
      logger.debug('CrmCallback', 'Effect triggered', {
        isAuthLoading,
        isAuthenticated,
        hasHandled: hasHandledCallback.current,
        selectedProvider,
      });
      if (isAuthLoading) {
        logger.debug('CrmCallback', 'Firebase auth still loading, waiting...');
        return;
      }

      if (!isAuthenticated) {
        logger.error(
          'CrmCallback',
          'User not authenticated after Firebase auth check. Redirecting to login.'
        );
        toast.error('Authentication session issue. Please log in again.');
        navigate('/login');
        return;
      }

      if (hasHandledCallback.current) {
        logger.debug('CrmCallback', 'Callback already handled, skipping.');
        return;
      }
      hasHandledCallback.current = true;
      logger.debug('CrmCallback', 'Proceeding to handle CRM callback code.');

      const code = searchParams.get('code');
      const oauthState = decodeCrmOAuthState(searchParams.get('state'));
      const provider = oauthState?.provider || selectedProvider;
      const loginDomain = oauthState?.loginDomain || null;

      if (!code) {
        logger.error('CrmCallback', 'No CRM authorization code found in URL.');
        toast.error('Authentication failed.');
        navigate('/login');
        return;
      }

      if (!provider) {
        logger.error('CrmCallback', 'No CRM provider selected.');
        toast.error('No CRM provider selected. Please try again.');
        navigate('/login');
        return;
      }

      if (getHandledCrmOauthCodes().has(code)) {
        logger.warn('CrmCallback', 'Skipping duplicate CRM OAuth callback code.', {
          provider,
          codePreview: `${code.slice(0, 6)}...`,
        });
        navigate('/dashboard');
        return;
      }

      markCrmOauthCodeHandled(code);

      if (oauthState?.provider && oauthState.provider !== selectedProvider) {
        setSelectedProvider(oauthState.provider);
      }

      logger.info('CrmCallback', 'CRM authorization code received, processing...', { provider });
      try {
        await handleCrmCallback(provider, code, { loginDomain });
        logger.info('CrmCallback', 'CRM connection successful. Navigating to dashboard.', {
          provider,
          loginDomain,
        });
        toast.success(`Successfully connected to ${provider}`);
        navigate('/dashboard');
      } catch (error) {
        logger.error('CrmCallback', 'Error during handleCrmCallback', { error, provider });
        toast.error(
          `Failed to complete ${provider} authentication. Please try again or contact support.`
        );
        navigate('/login');
      }
    };

    if (!isAuthLoading) {
      handleCallback();
    }
  }, [
    searchParams,
    navigate,
    handleCrmCallback,
    isAuthLoading,
    isAuthenticated,
    selectedProvider,
    setSelectedProvider,
  ]);

  return <FullScreenLoader message="Completing CRM connection..." showOverlay={false} />;
};
